import { CountryCode, type Language, type Listing } from '@ev-tech/search-modules-api';

import { createPushEnergyRange } from './utils';
import { type ListingDataTranslationKey, type ListingEnergyDescriptor } from './types';
import { CO2_EMISSION_UNIT_KEY, ENERGY_CERTIFICATE_INFO_VALUES, ENERGY_UNIT_KEY, EPC_UNIT } from './constants';
import { getHeatingMaterialTranslationKey, getHeatingTypeTranslationKey } from '../translationKeyUtils';
import { getEnergyCertificateTranslationKey } from '../energy';

const CONSUMPTION_FIELDS = [
  ['energyFinalDemand', 'search.listingData.energy.label.energyDemand'],
  ['finalEnergyConsumption', 'search.listingData.energy.label.finalEnergyConsumption'],
  ['finalPowerConsumption', 'search.listingData.energy.label.powerConsumption'],
  ['finalHeatingConsumption', 'search.listingData.energy.label.finalHeatingConsumption'],
  ['heatingConsumption', 'search.listingData.energy.label.heatingConsumption'],
] as const;
/**
 * Selects and formats the energy information available for a listing.
 *
 * Numeric values use `language` for formatting. Construction year is returned without a thousands separator. Values and units that require translation
 * provide their respective keys, while literal units are returned separately. Heating types
 * and materials are returned as individual descriptors, in their source order.
 */
export function getListingEnergyInformation(listing: Listing, { language }: { language: Language }): ListingEnergyDescriptor[] {
  const descriptors: ListingEnergyDescriptor[] = [];
  const pushRange = createPushEnergyRange({ descriptors, language });

  pushRange({
    fieldName: 'constructionYear',
    labelKey: 'search.listingData.label.constructionYear',
    value: listing.constructionYear,
    numberFormatOptions: { maximumFractionDigits: 0, useGrouping: false },
  });
  pushRange({
    fieldName: 'energyClassNormalized',
    labelKey: 'search.listingData.energy.label.energyEfficiencyClass',
    value: listing.energyClassNormalized,
  });

  const certificateValueKey = getEnergyCertificateTranslationKey(listing.propertyType, listing.countryAlpha2, listing.energyCertAvailable);
  if (certificateValueKey) {
    descriptors.push({
      fieldName: 'energyCertAvailable',
      labelKey: 'search.listingData.energy.label.energyCertificateAvailable',
      valueKey: certificateValueKey as ListingDataTranslationKey,
    });
  }

  const energyCertType = listing.energyCertType?.toLowerCase();
  if (energyCertType) {
    descriptors.push({
      fieldName: 'energyCertType',
      labelKey: 'search.listingData.energy.label.energyCertType',
      ...(ENERGY_CERTIFICATE_INFO_VALUES.has(energyCertType)
        ? { valueKey: `search.listingData.energy.label.${energyCertType}` as ListingDataTranslationKey }
        : { value: energyCertType }),
    });
  }

  for (const [fieldName, labelKey] of CONSUMPTION_FIELDS) {
    pushRange({ fieldName, labelKey, value: listing[fieldName], unitKey: ENERGY_UNIT_KEY });
  }

  for (const heatingType of listing.heatingType ?? []) {
    const valueKey = getHeatingTypeTranslationKey(heatingType);
    if (valueKey) {
      descriptors.push({
        fieldName: 'heatingType',
        labelKey: 'search.listingData.energy.label.heatingType',
        valueKey,
      });
    }
  }
  for (const heatingMaterial of listing.heatingMaterial ?? []) {
    const valueKey = getHeatingMaterialTranslationKey(heatingMaterial);
    if (valueKey) {
      descriptors.push({
        fieldName: 'heatingMaterial',
        labelKey: 'search.listingData.energy.label.heatingMaterial',
        valueKey,
      });
    }
  }
  pushRange({
    fieldName: 'energyEfficiencyFactor',
    labelKey: 'search.listingData.energy.label.energyEfficiencyFactor',
    value: listing.energyEfficiencyFactor,
  });
  pushRange({
    fieldName: 'co2EmissionRate',
    labelKey: 'search.listingData.energy.label.co2EmissionRate',
    value: listing.co2EmissionRate,
  });

  if (listing.heatProtectionClass) {
    descriptors.push({
      fieldName: 'heatProtectionClass',
      labelKey: 'search.listingData.energy.label.heatProtectionClass',
      value: listing.heatProtectionClass,
    });
  }

  if (listing.countryAlpha2 === CountryCode.BE) {
    pushRange({
      fieldName: 'co2Emission',
      labelKey: 'search.listingData.energy.label.co2Emission',
      value: listing.co2Emission,
      unitKey: CO2_EMISSION_UNIT_KEY,
    });
    if (listing.epCode) {
      descriptors.push({
        fieldName: 'epCode',
        labelKey: 'search.listingData.energy.label.epCode',
        value: listing.epCode,
        unit: EPC_UNIT,
      });
    }
  }

  const hasConsumptionValue = [
    listing.energyFinalDemand,
    listing.finalEnergyConsumption,
    listing.finalPowerConsumption,
    listing.finalHeatingConsumption,
    listing.heatingConsumption,
  ].some((value) => value != null);
  if (hasConsumptionValue && listing.isEnergyIncludeHotWater !== undefined) {
    descriptors.push({
      fieldName: 'isEnergyIncludeHotWater',
      labelKey: 'search.listingData.energy.label.energyIncludeHotWater',
      valueKey: listing.isEnergyIncludeHotWater ? 'search.listingData.label.yes' : 'search.listingData.label.no',
    });
  }

  return descriptors;
}
